import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { api } from '../api';

type Status = 'checking' | 'ok' | 'down';

/**
 * Top-of-page banner shown when the aue-mcp server is unreachable.
 *
 * Replaces the previous UX where every API call surfaced a raw
 * `500 Internal Server Error: (no body)` (or a `TypeError: Failed to fetch`
 * from the browser's fetch layer) and left the operator guessing.
 *
 * Behavior:
 *   - Polls `/api` on mount and every 10s.
 *   - While the service responds with a valid JSON body, renders nothing.
 *   - On a network error or non-2xx, renders a dismissable warning row.
 *   - Manual Retry button forces an immediate re-check.
 */
export function ServiceStatusBanner(): ReactElement | null {
  const [status, setStatus] = useState<Status>('checking');
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const check = async (): Promise<void> => {
      try {
        await api.getServiceInfo();
        if (!cancelled) setStatus('ok');
      } catch {
        if (!cancelled) setStatus('down');
      }
    };
    void check();
    const timer = setInterval(() => {
      void check();
    }, 10_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [retryNonce]);

  if (status !== 'down') return null;

  return (
    <div
      role="alert"
      data-testid="service-status-banner"
      className="w-full bg-rose-900 text-rose-100 border-b border-rose-700 px-6 py-2 text-sm flex items-center justify-between"
    >
      <span>
        Please start Service — the aue-mcp server is unreachable. Start it
        with <code className="bg-rose-950 px-1 rounded">npm run dev</code> in
        the <code className="bg-rose-950 px-1 rounded">aue-mcp</code> package.
      </span>
      <button
        type="button"
        onClick={() => setRetryNonce((n) => n + 1)}
        className="ml-4 underline hover:no-underline"
      >
        Retry
      </button>
    </div>
  );
}